import {
  useDeleteUserMutation,
  useForgotPasswordMutation,
  useResendActivationEmailMutation,
  useResendChangeEmailVerificationEmailMutation,
} from 'common/api/endpoints/user';
import { handleError } from 'common/api/utils/handleError';
import { User } from 'common/models';
import * as notificationService from 'common/services/notification';
import { useConfirmationModal } from 'features/confirmation-modal';
import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { Constants } from 'utils/constants';
import { getUserFullName } from 'utils/helperFunction';

export type ActionResponse = 'confirm' | 'success' | 'error';

type ActionResponseHandler = (response: ActionResponse) => void;
interface action {
  (user: User, onActionResponse?: ActionResponseHandler): void;
}

type ActionsType = {
  handleDelete: action;
  handlePasswordReset: action;
  resendEmailChangeVerification: action;
  resendEmailVerification: action;
};

const useUserActions = (): ActionsType => {
  const history = useHistory();
  const [sendForgotPasswordEmail] = useForgotPasswordMutation();
  const [deleteUser] = useDeleteUserMutation();
  const [resendActivationEmail] = useResendActivationEmailMutation();
  const [resendChangeEmailVerificationEmail] =
    useResendChangeEmailVerificationEmailMutation();
  const { openModal } = useConfirmationModal();

  const handlePasswordReset: action = useCallback(
    (user: User, onActionResponse?: ActionResponseHandler) => {
      const title = 'Reset password email';
      const body = `Send to '${user.email}'?`;

      const onConfirm = async () => {
        try {
          if (onActionResponse) {
            onActionResponse('confirm');
          }
          await sendForgotPasswordEmail({ email: user.email }).unwrap();
          notificationService.showSuccessMessage(
            `A reset password email was sent to ${user.email}.`,
          );
          if (onActionResponse) {
            onActionResponse('success');
          }
        } catch (error) {
          handleError(
            error,
            `There was an issue sending an email to ${user.email}.`,
          );
          if (onActionResponse) {
            onActionResponse('error');
          }
        }
      };

      openModal({ title, body, confirmButtonLabel: 'SEND', onConfirm });
    },
    [openModal, sendForgotPasswordEmail],
  );

  const handleDelete: action = useCallback(
    (user: User, onActionResponse?: ActionResponseHandler) => {
      const title = `Delete user '${getUserFullName(user)}'`;

      const onConfirm = async () => {
        try {
          if (onActionResponse) {
            onActionResponse('confirm');
          }
          await deleteUser(user.id).unwrap();
          history.push(
            `${Constants.routes.SETTINGS}${Constants.routes.USERS_LIST}`,
          );
          notificationService.showSuccessMessage(
            `User '${getUserFullName(user)}' was deleted.`,
          );
        } catch (error) {
          handleError(
            error,
            `There was an issue deleting user '${getUserFullName(user)}'.`,
          );
          if (onActionResponse) {
            onActionResponse('error');
          }
        }
      };

      openModal({ title, confirmButtonLabel: 'DELETE', onConfirm });
    },
    [openModal, deleteUser, history],
  );

  const resendEmailVerification: action = useCallback(
    (user: User, onActionResponse?: ActionResponseHandler) => {
      const title = 'Resend account activation email';
      const body = `Send to '${user.email}'?`;

      const onConfirm = async () => {
        try {
          if (onActionResponse) {
            onActionResponse('confirm');
          }
          await resendActivationEmail({ id: user.id }).unwrap();
          notificationService.showSuccessMessage(
            `An account activation email was sent to ${user.email}.`,
          );
          if (onActionResponse) {
            onActionResponse('success');
          }
        } catch (error) {
          handleError(
            error,
            `There was an issue sending an email to ${user.email}.`,
          );
          if (onActionResponse) {
            onActionResponse('error');
          }
        }
      };

      openModal({ title, body, confirmButtonLabel: 'SEND', onConfirm });
    },
    [openModal, resendActivationEmail],
  );

  const resendEmailChangeVerification: action = useCallback(
    (user: User, onActionResponse?: ActionResponseHandler) => {
      const title = 'Resend account verification email';
      const body = `Send to '${user.email}'?`;

      const onConfirm = async () => {
        try {
          if (onActionResponse) {
            onActionResponse('confirm');
          }
          await resendChangeEmailVerificationEmail({ id: user.id }).unwrap();
          notificationService.showSuccessMessage(
            `An account verification email was sent to ${user.email}.`,
          );
          if (onActionResponse) {
            onActionResponse('success');
          }
        } catch (error) {
          handleError(
            error,
            `There was an issue sending an email to ${user.email}.`,
          );
          if (onActionResponse) {
            onActionResponse('error');
          }
        }
      };

      openModal({ title, body, confirmButtonLabel: 'SEND', onConfirm });
    },
    [openModal, resendChangeEmailVerificationEmail],
  );

  return {
    handlePasswordReset,
    handleDelete,
    resendEmailVerification,
    resendEmailChangeVerification,
  };
};

export default useUserActions;
